/**
 * Bringing the staff over from the old system, in four steps.
 *
 *   1. Choose the spreadsheet (Excel or CSV) — or download the template.
 *   2. Match its columns: recognised headings are matched already (English
 *      or Arabic); anything can be changed, or left out.
 *   3. Check: the platform reads every row and says, before anything is
 *      saved, who will be added, who updated, and which rows it cannot take
 *      and why.
 *   4. Import, and see the result, with the problem rows to take away.
 *
 * An employee number already on the register updates that person with the
 * cells the sheet fills; it never makes a second person. Nobody is sent an
 * SMS or email by an import.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useHrImport } from "../../api/queries";
import { Button, Select } from "../../design/components";
import {
  autoMap,
  findHeader,
  IMPORT_FIELDS,
  FIELD_BY,
  parseCsv,
  prepareRows,
  templateCsv,
  type Cell,
  type ImportResponse,
} from "../../domain/hrImport";
import { t } from "../../i18n";
import { saveFile } from "../../lib/saveFile";
import { useHrAccess } from "./access";
import { Chip, h } from "./kit";
import { s } from "./shared";

type Step = "file" | "match" | "check" | "done";

async function readSheet(file: File): Promise<Cell[][]> {
  const name = file.name.toLowerCase();
  if (name.endsWith(".xlsx")) {
    const { default: readXlsxFile } = await import("read-excel-file/browser");
    return (await readXlsxFile(file)) as unknown as Cell[][];
  }
  if (name.endsWith(".csv") || name.endsWith(".txt") || file.type.startsWith("text/"))
    return parseCsv(await file.text());
  if (name.endsWith(".xls"))
    throw new Error(
      t("This is an old .xls file. Save it as .xlsx or CSV in Excel, then choose it again."),
    );
  throw new Error(t("Choose an Excel (.xlsx) or CSV file."));
}

export function ImportDialog({ onClose }: { onClose: () => void }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open && typeof d.showModal === "function") d.showModal();
  }, []);
  const { can } = useHrAccess();
  const imp = useHrImport();
  const [step, setStep] = useState<Step>("file");
  const [fileName, setFileName] = useState("");
  const [rows, setRows] = useState<Cell[][]>([]);
  const [header, setHeader] = useState(0);
  const [mapping, setMapping] = useState<(string | null)[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [update, setUpdate] = useState(true);
  const [check, setCheck] = useState<ImportResponse | null>(null);
  const [done, setDone] = useState<ImportResponse | null>(null);
  const [over, setOver] = useState(false);

  const headings = rows[header] ?? [];
  const prepared = useMemo(
    () => (step === "check" || step === "done" ? prepareRows(rows, header, mapping, { canPay: can.pay }) : []),
    [step, rows, header, mapping, can.pay],
  );
  const sendable = prepared.filter((r) => r.errors.length === 0);
  const localBad = prepared.filter((r) => r.errors.length > 0);
  const missingRequired = IMPORT_FIELDS.filter((f) => f.required && !mapping.includes(f.key));
  const payMapped = mapping.some((k) => k && FIELD_BY.get(k)?.pay);

  const load = async (file: File) => {
    setErr(null);
    try {
      const data = await readSheet(file);
      if (data.length < 2) throw new Error(t("The sheet has no rows under its headings."));
      const hRow = findHeader(data);
      setRows(data);
      setHeader(hRow);
      setMapping(autoMap(data[hRow]));
      setFileName(file.name);
      setStep("match");
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  };

  const runCheck = () => {
    const ready = prepareRows(rows, header, mapping, { canPay: can.pay }).filter((r) => r.errors.length === 0);
    setStep("check");
    setCheck(null);
    if (ready.length)
      imp.mutate(
        {
          rows: ready.map((r) => ({
            row: r.row,
            data: r.data,
            loginEmail: r.loginEmail,
            leaveBalance: r.leaveBalance,
          })),
          dryRun: true,
          updateExisting: update,
        },
        { onSuccess: setCheck },
      );
  };

  const runImport = () =>
    imp.mutate(
      {
        rows: sendable.map((r) => ({
          row: r.row,
          data: r.data,
          loginEmail: r.loginEmail,
          leaveBalance: r.leaveBalance,
        })),
        dryRun: false,
        updateExisting: update,
      },
      {
        onSuccess: (r) => {
          setDone(r);
          setStep("done");
        },
      },
    );

  const problems = () => {
    const server = new Map((done ?? check)?.results.map((x) => [x.row, x]) ?? []);
    const lines = [["Row", "Employee number", "Name", "Problem"].join(",")];
    const esc = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
    for (const r of prepared) {
      const sv = server.get(r.row);
      const why = r.errors.length ? r.errors.join("; ") : sv?.action === "failed" ? (sv.error ?? "") : "";
      if (why) lines.push([String(r.row), r.employeeNo, r.name, why].map(esc).join(","));
    }
    void saveFile(
      `staff-import-problems-${new Date().toISOString().slice(0, 10)}.csv`,
      "﻿" + lines.join("\r\n"),
      "text/csv",
    );
  };

  const server = check?.results ?? [];
  const serverFailed = server.filter((x) => x.action === "failed");
  const counts = check?.counts;

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      className={s.dialog}
      style={{ width: "min(1100px, 96vw)" }}
      data-testid="hr-import-dialog"
    >
      <div className={s.dialogForm}>
        <h2>📥 {t("Import staff from a spreadsheet")}</h2>
        <div className={h.fileMeta}>
          {(["file", "match", "check", "done"] as Step[]).map((st, i) => (
            <Chip key={st} colour={st === step ? "var(--blue)" : undefined}>
              {i + 1}. {t({ file: "Choose the file", match: "Match the columns", check: "Check", done: "Done" }[st])}
            </Chip>
          ))}
        </div>

        {step === "file" && (
          <>
            <label
              className={h.hero}
              style={{
                textAlign: "center",
                cursor: "pointer",
                borderStyle: "dashed",
                outline: over ? "2px solid var(--blue)" : undefined,
              }}
              onDragOver={(e) => {
                e.preventDefault();
                setOver(true);
              }}
              onDragLeave={() => setOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setOver(false);
                const f = e.dataTransfer.files[0];
                if (f) void load(f);
              }}
            >
              <span style={{ fontSize: 40 }}>📄</span>
              <b>{t("Drop the old system's staff export here, or click to choose it")}</b>
              <span className={s.dim}>
                {t("Excel (.xlsx) or CSV. First sheet only; English or Arabic headings.")}
              </span>
              <input
                type="file"
                accept=".xlsx,.csv,.txt,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                hidden
                data-testid="hr-import-file"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void load(f);
                }}
              />
            </label>
            {err && <p className={s.err}>{err}</p>}
            <div className={s.coverage}>
              <div>
                <b>{t("Starting from scratch?")}</b>{" "}
                {t("Download the template, fill one row per person, and bring it back here.")}{" "}
                <Button
                  size="sm"
                  onClick={() => void saveFile("sky-logics-staff-template.csv", templateCsv(), "text/csv")}
                  data-testid="hr-import-template"
                >
                  ⬇ {t("Download the template")}
                </Button>
              </div>
              <div className={s.dim}>
                {t(
                  "Only employee number, name, nationality, and hire date are required. Old leave balances can come too.",
                )}
              </div>
            </div>
          </>
        )}

        {step === "match" && (
          <>
            <p className={s.note}>
              {t(
                "{file}: {n} rows, headings on row {h}. Check the matched columns, and change or skip any.",
                {
                  file: fileName,
                  n: rows.length - header - 1,
                  h: header + 1,
                },
              )}
            </p>
            <div style={{ maxHeight: "50vh", overflow: "auto", border: "1px solid var(--line)", borderRadius: 8 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "start" }}>{t("Column in the file")}</th>
                    <th style={{ textAlign: "start" }}>{t("First values")}</th>
                    <th style={{ textAlign: "start" }}>{t("Goes into")}</th>
                  </tr>
                </thead>
                <tbody>
                  {headings.map((hd, col) => (
                    <tr key={col} data-testid="hr-import-col">
                      <td style={{ padding: "6px 8px" }}>
                        <b>{String(hd ?? `(${t("column {n}", { n: col + 1 })})`)}</b>
                      </td>
                      <td style={{ padding: "6px 8px" }} className={s.dim}>
                        {rows
                          .slice(header + 1, header + 4)
                          .map((r) =>
                            r[col] instanceof Date ? (r[col] as Date).toISOString().slice(0, 10) : String(r[col] ?? ""),
                          )
                          .filter(Boolean)
                          .join(" · ")
                          .slice(0, 60)}
                      </td>
                      <td style={{ padding: "6px 8px" }}>
                        <Select
                          value={mapping[col] ?? ""}
                          onChange={(e) => {
                            const next = [...mapping];
                            const v = e.target.value || null;
                            if (v) next.forEach((k, i) => k === v && (next[i] = null));
                            next[col] = v;
                            setMapping(next);
                          }}
                          data-testid={`hr-import-map-${col}`}
                        >
                          <option value="">{t("— not imported —")}</option>
                          {IMPORT_FIELDS.filter((f) => can.pay || !f.pay).map((f) => (
                            <option key={f.key} value={f.key}>
                              {t(f.label)}
                              {f.required ? " *" : ""}
                            </option>
                          ))}
                        </Select>
                        {mapping[col] && <span className={s.dim}> ✓</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {missingRequired.length > 0 && (
              <p className={s.err}>
                {t("Still needed: {fields}.", { fields: missingRequired.map((f) => t(f.label)).join(", ") })}
              </p>
            )}
            {!can.pay && <p className={s.note}>{t("Pay columns are left out: your level cannot change pay.")}</p>}
            {can.pay && payMapped && (
              <p className={s.note}>{t("Pay will be imported. Only people with the pay permission will see it.")}</p>
            )}
            <div className={s.formFoot}>
              <Button
                variant="primary"
                disabled={missingRequired.length > 0}
                onClick={runCheck}
                data-testid="hr-import-check"
              >
                {t("Check the rows")} →
              </Button>
              <Button onClick={() => setStep("file")}>{t("Choose another file")}</Button>
            </div>
          </>
        )}

        {step === "check" && (
          <>
            {imp.isPending && !check && <p className={s.note}>{t("The platform is reading every row…")}</p>}
            {imp.error && <p className={s.err}>{imp.error instanceof Error ? imp.error.message : String(imp.error)}</p>}
            {(check || sendable.length === 0) && (
              <>
                <div className={h.stats} data-testid="hr-import-summary">
                  <div className={h.stat} style={{ ["--tint" as string]: "var(--green)", cursor: "default" }}>
                    <span className={h.statN} data-testid="hr-import-add">
                      {counts?.added ?? 0}
                    </span>
                    <span className={h.statLabel}>{t("will be added")}</span>
                  </div>
                  <div className={h.stat} style={{ ["--tint" as string]: "var(--blue)", cursor: "default" }}>
                    <span className={h.statN}>{counts?.updated ?? 0}</span>
                    <span className={h.statLabel}>{t("will be updated")}</span>
                  </div>
                  <div className={h.stat} style={{ ["--tint" as string]: "var(--dim)", cursor: "default" }}>
                    <span className={h.statN}>{(counts?.unchanged ?? 0) + (counts?.skipped ?? 0)}</span>
                    <span className={h.statLabel}>{t("already up to date or skipped")}</span>
                  </div>
                  <div className={h.stat} style={{ ["--tint" as string]: "var(--red)", cursor: "default" }}>
                    <span className={h.statN} data-testid="hr-import-bad">
                      {localBad.length + serverFailed.length}
                    </span>
                    <span className={h.statLabel}>{t("cannot be imported")}</span>
                  </div>
                </div>
                <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
                  <input type="checkbox" checked={update} onChange={(e) => setUpdate(e.target.checked)} />
                  {t("Update people already on the register (matched by employee number)")}
                </label>
                <Problems prepared={prepared} server={server} />
                <p className={s.note}>
                  {t("Nothing is saved yet. Imports don't send SMS or email.")}
                </p>
                <div className={s.formFoot}>
                  <Button
                    variant="primary"
                    disabled={imp.isPending || !check || check.counts.added + check.counts.updated === 0}
                    onClick={runImport}
                    data-testid="hr-import-go"
                  >
                    ✓ {t("Import {n} people", { n: (check?.counts.added ?? 0) + (check?.counts.updated ?? 0) })}
                  </Button>
                  <Button onClick={() => setStep("match")}>← {t("Back to the columns")}</Button>
                  {localBad.length + serverFailed.length > 0 && (
                    <Button onClick={problems}>⬇ {t("Save the problem rows")}</Button>
                  )}
                </div>
              </>
            )}
          </>
        )}

        {step === "done" && done && (
          <>
            <div className={h.hero} data-testid="hr-import-done">
              <h2>🎉 {t("Imported")}</h2>
              <p>
                {t("{a} added and {u} updated. {f} rows could not be imported.", {
                  a: done.counts.added,
                  u: done.counts.updated,
                  f: done.counts.failed + localBad.length,
                })}
              </p>
            </div>
            <Problems prepared={prepared} server={done.results} />
            <div className={s.formFoot}>
              <Button variant="primary" onClick={onClose} data-testid="hr-import-close">
                {t("See the workforce")}
              </Button>
              {done.counts.failed + localBad.length > 0 && (
                <Button onClick={problems}>⬇ {t("Save the problem rows")}</Button>
              )}
            </div>
          </>
        )}
        {step !== "done" && (
          <div className={s.formFoot} style={{ justifyContent: "flex-end" }}>
            <Button onClick={onClose}>{t("Cancel")}</Button>
          </div>
        )}
      </div>
    </dialog>
  );
}

function Problems({
  prepared,
  server,
}: {
  prepared: ReturnType<typeof prepareRows>;
  server: ImportResponse["results"];
}) {
  const byRow = new Map(server.map((x) => [x.row, x]));
  const shown = prepared
    .map((r) => {
      const sv = byRow.get(r.row);
      const errors = [...r.errors, ...(sv?.action === "failed" && sv.error ? [sv.error] : [])];
      const warnings = [...r.warnings, ...(sv?.warning ? [sv.warning] : [])];
      return { r, sv, errors, warnings };
    })
    .filter((x) => x.errors.length || x.warnings.length);
  if (!shown.length) return <p className={s.ok}>✓ {t("Every row reads cleanly.")}</p>;
  return (
    <ul className={h.list} style={{ maxHeight: "36vh", overflow: "auto" }} data-testid="hr-import-problems">
      {shown.slice(0, 300).map(({ r, errors, warnings }) => (
        <li key={r.row} className={h.item} style={{ borderColor: errors.length ? "var(--red)" : "var(--amber)" }}>
          <span className={h.when}>{t("row {n}", { n: r.row })}</span>
          <span className={h.itemMain}>
            <b>{[r.employeeNo, r.name].filter(Boolean).join(" · ") || "—"}</b>
            <span>{[...errors.map((e) => `✗ ${e}`), ...warnings.map((w) => `⚠ ${w}`)].join("  ")}</span>
          </span>
          <Chip colour={errors.length ? "var(--red)" : "var(--amber)"}>
            {errors.length ? t("not imported") : t("imported, with a note")}
          </Chip>
        </li>
      ))}
    </ul>
  );
}
